
import React from 'react';
import Navbar from '@/components/navbar';
import Footer from '@/components/Footer';
import { Separator } from '@/components/ui/separator';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow pt-24 pb-20">
        <div className="container px-4 mx-auto">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl font-bold mb-8">Privacy Policy</h1>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">1. Introduction & Scope</h2>
              <p className="text-muted-foreground mb-4">
                Welcome to Dropify. Your privacy is important to us. This Privacy Policy outlines how we collect, use, 
                and protect your personal information.
              </p>
              <p className="text-muted-foreground mb-4">
                By using Dropify, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>
            
            <Separator className="my-6" />
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">2. What Data We Collect</h2>
              <p className="text-muted-foreground mb-2">Dropify collects the following data when you use our platform:</p>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground mb-4">
                <li>
                  <span className="font-medium">Account Information:</span> Email, username, and password for authentication.
                </li>
                <li>
                  <span className="font-medium">Spotify Integration:</span> We collect Spotify track URLs when artists upload tracks. 
                  We do NOT collect or track actual Spotify likes.
                </li>
                <li>
                  <span className="font-medium">Payment Data:</span> Payment details are processed securely via Stripe 
                  (we do not store your credit card information).
                </li>
                <li>
                  <span className="font-medium">Usage Data:</span> Tracks you upload and download activity.
                </li>
              </ul>
            </section>
            
            <Separator className="my-6" />
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">3. How We Use Your Data</h2>
              <p className="text-muted-foreground mb-2">We use the collected data to:</p>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground mb-4">
                <li>
                  <span className="text-green-500">✅</span> Provide access to the platform
                </li>
                <li>
                  <span className="text-green-500">✅</span> Track uploaded/downloaded tracks
                </li>
                <li>
                  <span className="text-green-500">✅</span> Improve user experience and analytics
                </li>
                <li>
                  <span className="text-green-500">✅</span> Securely process payments through Stripe
                </li>
              </ul>
              <p className="text-muted-foreground mb-4">
                We never sell or share your data with third parties for advertising purposes.
              </p>
            </section>
            
            <Separator className="my-6" />
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">4. Third-Party Services (Spotify & Stripe)</h2>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground mb-4">
                <li>
                  <span className="font-medium">Spotify API:</span> Dropify allows users to provide Spotify track links, 
                  but we do NOT track actual likes due to Spotify's Terms of Service.
                </li>
                <li>
                  <span className="font-medium">Stripe Payments:</span> If you purchase a subscription, 
                  payments are securely processed via Stripe.
                </li>
              </ul>
              <p className="text-amber-500 font-medium mb-4">
                ⚠️ Your use of Spotify and Stripe is subject to their respective privacy policies.
              </p>
            </section>
            
            <Separator className="my-6" />
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">5. Data Storage & Security</h2>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground mb-4">
                <li>
                  User data is stored securely in Supabase databases and cloud storage.
                </li>
                <li>
                  We implement industry-standard security measures to protect your data.
                </li>
                <li>
                  Only authorized users can access their own data through authentication.
                </li>
              </ul>
            </section>
            
            <Separator className="my-6" />
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">6. Cookies & Tracking Technologies</h2>
              <p className="text-muted-foreground mb-2">Dropify may use cookies to:</p>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground mb-4">
                <li>
                  <span>🍪</span> Improve user experience
                </li>
                <li>
                  <span>📊</span> Track page visits & downloads
                </li>
                <li>
                  <span>🔒</span> Maintain session security
                </li>
              </ul>
              <p className="text-muted-foreground mb-4">
                Users can disable cookies in their browser settings, but this may affect functionality.
              </p>
            </section>
            
            <Separator className="my-6" />
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">7. User Rights & Data Deletion Requests</h2>
              <p className="text-muted-foreground mb-2">Users have the right to:</p>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground mb-4">
                <li>
                  <span>🔹</span> Request access to their stored data
                </li>
                <li>
                  <span>🔹</span> Request deletion of their account and data (except for required records like payment history)
                </li>
              </ul>
              <p className="text-muted-foreground mb-4">
                To request data deletion, contact support@dropify.com with your request.
              </p>
            </section>
            
            <Separator className="my-6" />
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">8. Policy Updates</h2>
              <p className="text-muted-foreground mb-4">
                We may update this Privacy Policy from time to time. Users will be notified of any significant changes.
              </p>
            </section>
            
            <Separator className="my-6" />
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">9. Contact Information</h2>
              <p className="text-muted-foreground mb-4">
                For privacy-related questions, please contact:<br />
                <span className="text-primary">📧 support@dropify.com</span>
              </p>
            </section>
            
            <div className="mt-10 pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
